import { ApiResponse, axiosInstance } from '@/lib/axios';
import { QueryConfig } from '@/lib/react-query';
import { queryOptions, useQuery } from '@tanstack/react-query';

export type TGetListLocationGoogleReviewData = {
  name: string;
  title: string;
}[];
export type TGetListLocationGoogleReviewParams = {
  state: string;
};
export type TGetListLocationGoogleReviewResponse = ApiResponse<TGetListLocationGoogleReviewData>;

export const getListLocationGoogleReview = async (
  params: TGetListLocationGoogleReviewParams
): Promise<TGetListLocationGoogleReviewResponse> => {
  const url = '/integration/google-review/location';

  return axiosInstance.get(url, { params });
};

export const GET_LIST_LOCATION_GOOGLE_REVIEW_QUERY_KEY = 'get-list-location-google-review';

export const getListLocationGoogleReviewQueryOptions = <TData = TGetListLocationGoogleReviewData>(
  params: TGetListLocationGoogleReviewParams,
  select?: (data: TGetListLocationGoogleReviewResponse) => TData
) => {
  return queryOptions({
    queryKey: [
      GET_LIST_LOCATION_GOOGLE_REVIEW_QUERY_KEY,
      {
        state: params?.state,
      },
    ],
    queryFn: () => getListLocationGoogleReview(params),
    select,
  });
};

// Overload when select is provided - returns transformed type
export function useGetListLocationGoogleReviewQuery<TData>(options: {
  params: TGetListLocationGoogleReviewParams;
  queryConfig?: QueryConfig<typeof getListLocationGoogleReviewQueryOptions<TData>>;
  select: (data: TGetListLocationGoogleReviewResponse) => TData;
}): ReturnType<typeof useQuery<TData>>;

// Overload when select is not provided - returns raw API response (even if generic is provided)
export function useGetListLocationGoogleReviewQuery(options: {
  params: TGetListLocationGoogleReviewParams;
  queryConfig?: QueryConfig<typeof getListLocationGoogleReviewQueryOptions<TGetListLocationGoogleReviewResponse>>;
}): ReturnType<typeof useQuery<TGetListLocationGoogleReviewResponse>>;

// Implementation
export function useGetListLocationGoogleReviewQuery<TData = TGetListLocationGoogleReviewResponse>({
  queryConfig,
  params,
  select,
}: {
  params: TGetListLocationGoogleReviewParams;
  queryConfig?: QueryConfig<
    typeof getListLocationGoogleReviewQueryOptions<TData | TGetListLocationGoogleReviewResponse>
  >;
  select?: (data: TGetListLocationGoogleReviewResponse) => TData;
}) {
  return useQuery({
    ...getListLocationGoogleReviewQueryOptions(params, select),
    ...queryConfig,
  }) as ReturnType<
    typeof useQuery<TData extends TGetListLocationGoogleReviewResponse ? TGetListLocationGoogleReviewResponse : TData>
  >;
}
